import { Box, Container, Grid, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { GridColumnOrderChangeParams, GridValidRowModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE } from 'route/RouteEnums';
import { useAppDispatch } from 'store/hooks';
import { getMdacPatientsList } from 'store/slices/adminSlice';
import COLORS from 'theme/colors';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { AuthContext } from 'utils/AuthContext';
import CustomDataGrid, { CustomDataGridProps } from 'utils/customDataGrid';
import { formatAppointmentDateTime, formatDate, formatPhoneNumber } from 'utils/dataFormatter';
import {
  AgeFilter,
  BillingStatusFilter,
  CreditCardStatusFilter,
  CustodyTypeFilter,
  DateTimeFilterOperators,
  GenderFilter,
  LocationFilter,
  MigratedFilter,
  OrganizationTypeFilter,
  PaymentFilter,
  ProviderFilter,
  StateFilter,
  useProviderFilterOperators,
} from 'utils/gridOperators';
import { validateSession } from 'services/validateSession';
import { render } from '@testing-library/react';
import { useProviders } from 'utils/useProviders';

function PatientDatabaseReport() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, setUserDetails } = useContext(AuthContext);
  const pageKey = 'mdac-patient-database';
  const [patientData, setPatientData] = useState<any[] | null>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState(0);

  const getToolTipTitle = (params: any) => {
    const status =
      params.row.patientMigrationStatus !== null ? params.row.patientMigrationStatus : '';
    return <>{params.row.patientFullName + ' ' + status}</>;
  };
  const columns = [
    {
      field: 'patientFullName',
      width: 200,
      type: 'string',
      headerName: 'Patient Name',
      hideable: false,
      sortable: true,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <span>
            <Tooltip
              title={getToolTipTitle(params)}
              arrow
              placement='bottom-start'
              enterTouchDelay={0}
            >
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#101828',
                  fontFamily: 'Graphik-Regular, sans-serif',
                }}
              >
                {params.row.patientFullName}
                <span
                  style={{
                    color: '#1570EF',
                    marginLeft: '5px',
                    fontSize: '11px',
                    fontWeight: 700,
                    lineHeight: '24px',
                  }}
                >
                  {params.row.patientMigrationStatus === 'Self'
                    ? ''
                    : params.row.patientMigrationStatus}
                </span>
              </span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      field: 'patientAge',
      width: 80,
      type: 'number',
      headerName: 'Age',
      sortable: true,
      filterable: true,
      filterOperators: AgeFilter,
    },
    {
      field: 'patientDateOfBirth',
      width: 120,
      type: 'string',
      headerName: 'Date of Birth',
      sortable: true,
      filterable: true,
      filterOperators: DateTimeFilterOperators,
    },
    {
      field: 'patientGender',
      width: 100,
      type: 'string',
      headerName: 'Biological Gender',
      sortable: true,
      filterable: true,
      filterOperators: GenderFilter,
    },
    {
      field: 'patientCity',
      width: 120,
      type: 'string',
      headerName: 'City',
      sortable: true,
      filterable: false,
    },
    {
      field: 'patientZipcode',
      width: 100,
      type: 'string',
      headerName: 'ZIP Code',
      sortable: true,
      filterable: false,
    },
    {
      field: 'patientState',
      width: 120,
      type: 'string',
      headerName: 'State',
      sortable: true,
      filterable: true,
      filterOperators: StateFilter,
    },
    {
      field: 'patientPreferredLocation',
      width: 150,
      type: 'string',
      headerName: 'Preferred clinic location',
      sortable: true,
      filterable: true,
      filterOperators: LocationFilter,
      valueFormatter: (params: any) => params?.value || '',
      valueGetter: (params: any) => params?.value || '',
      renderCell: (params: any) => {
        if (!params?.value) return '';
        const addressParts = params?.value?.split(',').map((part: string) => part?.trim());
        const city = addressParts[addressParts?.length - 2];
        return city;
      },
    },
    {
      field: 'isActiveCustody',
      width: 120,
      type: 'string',
      headerName: 'Active custody agreement',
      sortable: true,
      filterable: true,
      filterOperators: CustodyTypeFilter,
    },

    {
      field: 'patientMigrationStatus',
      width: 120,
      type: 'string',
      headerName: 'Migration Status',
      sortable: true,
      filterable: true,
      filterOperators: MigratedFilter,
    },

    {
      field: 'patientRegistrationDate',
      width: 150,
      type: 'date',
      headerName: 'Registration Date',
      sortable: true,
      filterable: true,
      filterOperators: DateTimeFilterOperators,
      valueFormatter: (params: any) => formatDate(params?.value),
      valueGetter: (params: any) => params?.value,
      renderCell: (params: any) => {
        return params?.value ? (
          <>
            {' '}
            {dayjs
              .utc(params?.value)
              .local()
              .format('MM/DD/YYYY')
              .substring(0, 10)}
          </>
        ) : (
          ''
        );
      },
    },
    {
      field: 'primaryGuardianFullName',
      width: 150,
      type: 'string',
      headerName: 'Primary Guardian Name',
      sortable: true,
      filterable: false,
    },
    {
      field: 'primaryGuardianEmail',
      width: 180,
      type: 'string',
      headerName: 'Primary Guardian Email',
      sortable: true,
      filterable: false,
    },
    {
      field: 'primaryGuardianRelationship',
      width: 180,
      type: 'string',
      headerName: 'Primary Guardian Relation',
      sortable: true,
      filterable: false,
    },
    {
      field: 'primaryGuardianPhoneNumber',
      width: 140,
      type: 'phone',
      headerName: 'Primary Guardian Phone',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },
    {
      field: 'secondaryGuardianFullName',
      width: 150,
      type: 'string',
      headerName: 'Secondary Guardian Name',
      sortable: true,
      filterable: false,
    },

    {
      field: 'secondaryGuardianEmail',
      width: 180,
      type: 'string',
      headerName: 'Secondary Guardian Email',
      sortable: true,
      filterable: false,
    },
    {
      field: 'secondaryGuardianRelationship',
      width: 180,
      type: 'string',
      headerName: 'Secondary Guardian Relation',
      sortable: true,
      filterable: false,
    },
    {
      field: 'secondaryGuardianPhoneNumber',
      width: 140,
      type: 'phone',
      headerName: 'Secondary Guardian Phone',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },
    {
      field: 'emergencyGuardianFullName',
      width: 150,
      type: 'string',
      headerName: 'Emergency Contact Name',
      sortable: true,
      filterable: false,
    },
    {
      field: 'emergencyGuardianRelationship',
      width: 180,
      type: 'string',
      headerName: 'Emergency Contact Relationship',
      sortable: true,
      filterable: false,
    },
    {
      field: 'emergencyGuardianPhoneNumber',
      width: 140,
      type: 'phone',
      headerName: 'Emergency Contact Phone',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },

    {
      field: 'providerFullName',
      width: 120,
      type: 'string',
      headerName: 'PCP Name',
      sortable: true,
      filterable: false,
    },
    {
      field: 'providerOfficeName',
      width: 150,
      type: 'string',
      headerName: 'PCP Practice or Organization Name',
      sortable: true,
      filterable: false,
    },
    {
      field: 'providerOfficeFaxNumber',
      width: 140,
      type: 'phone',
      headerName: 'PCP Fax No',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },
    {
      field: 'providerOfficePhoneNumber',
      width: 140,
      type: 'phone',
      headerName: 'PCP Phone No',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },
    {
      field: 'counselorFullName',
      width: 150,
      type: 'string',
      headerName: 'Counselor Name',
      sortable: true,
      filterable: false,
    },
    {
      field: 'counselorOfficeName',
      width: 150,
      type: 'string',
      headerName: 'Counselor Practice or Organization Name',
      sortable: true,
      filterable: false,
    },
    {
      field: 'counselorOfficeFaxNumber',
      width: 140,
      type: 'phone',
      headerName: 'Counselor Fax No',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },
    {
      field: 'counselorOfficePhoneNumber',
      width: 140,
      type: 'phone',
      headerName: 'Counselor Phone No',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },
    {
      field: 'pharmacyName',
      width: 150,
      type: 'string',
      headerName: 'Pharmacy Name',
      sortable: true,
      filterable: false,
    },
    {
      field: 'pharmacyPhoneNumber',
      width: 140,
      type: 'phone',
      headerName: 'Pharmacy Phone Number',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },
    {
      field: 'pharmacyZipCode',
      width: 100,
      type: 'string',
      headerName: 'Pharmacy Zipcode',
      sortable: true,
      filterable: false,
    },
    {
      field: 'pharmacyAddress',
      width: 180,
      type: 'string',
      headerName: 'Pharmacy Address',
      sortable: true,
      filterable: false,
    },
    {
      field: 'primaryInsuranceCarrier',
      width: 150,
      type: 'string',
      headerName: 'Primary Insurance',
      sortable: true,
      filterable: false,
    },
    {
      field: 'secondaryInsuranceCarrier',
      width: 150,
      type: 'string',
      headerName: 'Secondary Insurance',
      sortable: true,
      filterable: false,
    },
    {
      field: 'paymentCreditCardOnfileStatus',
      width: 180,
      type: 'string',
      headerName: 'Credit Card on file',
      sortable: true,
      filterable: false,
    },
    {
      field: 'paymentCreditCardStatus',
      width: 150,
      type: 'string',
      headerName: 'Credit Card Status',
      sortable: true,
      filterable: true,
      filterOperators: CreditCardStatusFilter,
    },
    {
      field: 'paymentBillingStatus',
      width: 150,
      type: 'string',
      headerName: 'Billing Status',
      sortable: true,
      filterable: true,
      filterOperators: BillingStatusFilter,
    },
    {
      field: 'paymentType',
      width: 120,
      type: 'string',
      headerName: 'Payment Type',
      sortable: true,
      filterable: true,
      filterOperators: PaymentFilter,
    },
    {
      field: 'upcomingAppointmentDateTime',
      width: 180,
      type: 'appDateTime',
      headerName: 'Upcoming Appointment',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatAppointmentDateTime(params?.value),
      valueGetter: (params: any) => params?.value,
    },
    {
      field: 'upcomingAppointmentStatus',
      width: 150,
      type: 'string',
      headerName: 'Appointment Status',
      sortable: true,
      filterable: false,
    },
    {
      field: 'upcomingAppointmentConsultationType',
      width: 180,
      type: 'string',
      headerName: 'Consultation Type',
      sortable: true,
      filterable: false,
    },
    {
      field: 'upcomingAppointmentProviderName',
      width: 150,
      type: 'string',
      headerName: 'Provider Name',
      sortable: true,
      filterable: true,
      filterOperators: ProviderFilter,
    },
    {
      field: 'upcomingAppointmentOfficeAddress',
      width: 110,
      type: 'string',
      headerName: 'Appointment Location',
      sortable: true,
      filterable: true,
      filterOperators: LocationFilter,
    },
  ]; // ✅
  const getMdacPatients = async () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm: any = today.getMonth() + 1; // Months start at 0!
    let dd: any = today.getDate();

    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;
    const formattedToday = dd + '-' + mm + '-' + yyyy;
    const data = {
      date: formattedToday,
    };
    await dispatch(getMdacPatientsList(data))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setPatientData(response?.data);
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    setSelectedTab(0);
    getMdacPatients();
    window.onpopstate = () => {
      navigate(ROUTE.ADMIN_PATIENT_REPORT);
    };
  }, [dispatch, selectedTab, user?.userId]);

  const rowSelect = async (params: any) => {
    await validateSession();
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${params.row.patientId}`, '_blank');
    const data = {
      ...user,
      patientId: params.row.patientId,
    };
    setUserDetails(data);
  };

  return (
    <Container maxWidth='lg'>
      <Grid item xs={12}>
        <Typography
          variant='h5'
          position={'relative'}
          width={'fit-content'}
          sx={{ paddingTop: 6, textTransform: 'capitalize' }}
        >
          Patient Database
        </Typography>
      </Grid>
      <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
        <CustomDataGrid
          centered
          rows={patientData ? patientData : []}
          columns={columns}
          dashBordKey={pageKey + '_' + selectedTab}
          onRowSelect={rowSelect}
          userId={user.userId}
          setting={{ disableExport: !user?.permissions.includes('EXPORT_DATA') }}
        />
      </Grid>
    </Container>
  );
}

export default PatientDatabaseReport;
